import React, { useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import Logo from './Logo';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState<string | null>(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isQuoteGeneratorPage = location.pathname === '/quote-generator';

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const productCategories = [
    { title: "Full Catalogue", to: "/catalog" },
    { title: "Ball Bearings", to: "/products/ball-bearings" },
    { title: "Roller Bearings", to: "/products/roller-bearings" },
    { title: "Thrust Bearings", to: "/products/thrust-bearings" },
    { title: "Precision Bearings", to: "/products/precision-bearings" },
    { title: "Mounted Units", to: "/products/mounted-units" },
    { title: "Custom Solutions", to: "/products/custom-solutions" },
  ];

  const industryCategories = [
    { title: "Automotive", to: "/industries/automotive" },
    { title: "Aerospace", to: "/industries/aerospace" },
    { title: "Steel & Metal", to: "/industries/steel-metal" },
    { title: "Mining & Cement", to: "/industries/mining-cement" },
    { title: "Food & Beverage", to: "/industries/food-beverage" },
  ];

  const NavLink = ({ to, children, className = "", onClick }: { to: string, children: React.ReactNode, className?: string, onClick?: () => void }) => {
    const isExternal = to.startsWith('#');
    const isActive = location.pathname === to || (isHomePage && to === '/');

    const handleClick = (e: React.MouseEvent) => {
      if (onClick) onClick();
      if (isExternal && isHomePage) {
        e.preventDefault();
        const el = document.getElementById(to.substring(1));
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
    };

    if (isExternal && !isHomePage) {
      return (
        <Link to={`/${to}`} className={`hover:text-slate-900 transition-colors ${className}`} onClick={onClick}>
          {children}
        </Link>
      );
    }

    if (isExternal) {
      return (
        <a href={to} className={`hover:text-slate-900 transition-colors ${className}`} onClick={handleClick}>
          {children}
        </a>
      );
    }

    return (
      <Link 
        to={to} 
        className={`${isActive ? "text-red-600" : "hover:text-slate-900"} transition-colors ${className}`}
        onClick={onClick}
      >
        {children}
      </Link>
    );
  };

  const NavDropdown = ({ title, items, to }: { title: string, items: { title: string, to: string }[], to: string }) => {
    const isHovered = activeDropdown === title;

    return (
      <div 
        className="relative py-4"
        onMouseEnter={() => setActiveDropdown(title)}
        onMouseLeave={() => setActiveDropdown(null)}
      >
        <div className="flex items-center gap-1 cursor-pointer group">
          <NavLink to={to} className="!p-0">{title}</NavLink>
          <ChevronDown 
            size={14} 
            className={`transition-transform duration-300 ${isHovered ? "rotate-180 text-red-600" : "text-slate-400"}`} 
          />
        </div>

        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              className="absolute top-full left-1/2 -translate-x-1/2 w-64 bg-white border border-slate-200 shadow-xl rounded-sm p-2 z-50"
            >
              <div className="grid gap-1">
                {items.map((item, idx) => (
                  <Link
                    key={idx}
                    to={item.to}
                    className="block px-4 py-2 text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-red-600 hover:bg-slate-50 transition-colors"
                    onClick={() => {
                      if (item.to.startsWith('#')) {
                        const el = document.getElementById(item.to.substring(1));
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }
                      setActiveDropdown(null);
                    }}
                  >
                    {item.title}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const MobileNavDropdown = ({ title, items, to }: { title: string, items: { title: string, to: string }[], to: string }) => {
    const isOpenDropdown = activeMobileDropdown === title;

    return (
      <div className="flex flex-col">
        <button 
          onClick={() => setActiveMobileDropdown(isOpenDropdown ? null : title)}
          className="flex items-center justify-between px-4 py-3 text-slate-500 font-bold uppercase tracking-widest text-[12px]"
        >
          <span>{title}</span>
          <ChevronDown size={16} className={`transition-transform duration-300 ${isOpenDropdown ? "rotate-180" : ""}`} />
        </button>
        
        <AnimatePresence>
          {isOpenDropdown && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden bg-white/50 rounded"
            >
              <div className="flex flex-col py-2">
                {items.map((item, idx) => (
                  <Link
                    key={idx}
                    to={item.to}
                    className="px-8 py-2 text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-red-600"
                    onClick={() => {
                      setIsOpen(false);
                      if (item.to.startsWith('#')) {
                        const el = document.getElementById(item.to.substring(1));
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    {item.title}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  if (isQuoteGeneratorPage) return null;

  return (
    <nav className="fixed w-full z-50 top-0 left-0 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-12 h-20 flex items-center justify-between shrink-0 border-b border-slate-200">
        <Link to="/" className="flex items-center">
          <Logo className="origin-left" />
        </Link>
        
        <div className="hidden lg:flex gap-10 items-center text-sm font-medium uppercase tracking-widest text-slate-500">
          <NavLink to="/">Home</NavLink>
          <NavLink to="/about">About</NavLink>
          <NavLink to="/catalog" className="text-red-600 font-bold border border-red-200 bg-red-50 px-4 py-2 rounded-lg hover:bg-red-100 hover:border-red-300">Catalogue</NavLink>
          <NavDropdown title="Products" items={productCategories} to="#services" />
          <NavDropdown title="Industries" items={industryCategories} to="#industries" />
          <NavLink to="/contact">Contact</NavLink>
        </div>
        


        <button 
          className="lg:hidden flex items-center justify-center w-10 h-10 bg-slate-50 border border-slate-200 rounded-sm text-slate-900 transition-all hover:bg-slate-100 shrink-0" 
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      
      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40"
              onClick={() => setIsOpen(false)}
            />
            <motion.div 
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="lg:hidden fixed top-0 right-0 h-screen w-[280px] sm:w-[320px] bg-white border-l border-slate-200 p-4 flex flex-col gap-1 shadow-2xl z-50 overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-8 px-2">
                <Logo className="origin-left" />
                <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-slate-50 rounded-full transition-colors">
                  <X size={20} className="text-slate-400" />
                </button>
              </div>

              <NavLink 
                to="/" 
                onClick={() => setIsOpen(false)}
                className="px-4 py-3 font-bold text-slate-500 uppercase tracking-widest text-[12px] hover:bg-slate-50 rounded-sm"
              >
                Home
              </NavLink>
              <NavLink 
                to="/about" 
                onClick={() => setIsOpen(false)}
                className="px-4 py-3 font-bold uppercase tracking-widest text-[12px] text-slate-500 hover:bg-slate-50 rounded-sm"
              >
                About
              </NavLink>
              <NavLink 
                to="/catalog" 
                onClick={() => setIsOpen(false)}
                className="px-4 py-3 font-bold uppercase tracking-widest text-[12px] text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 mt-2 text-center"
              >
                Catalogue
              </NavLink>
            
            <div className="h-px bg-slate-100 my-1 mx-2" />
            <MobileNavDropdown title="Products" items={productCategories} to="#services" />
            <MobileNavDropdown title="Industries" items={industryCategories} to="#industries" />
            <div className="h-px bg-slate-100 my-1 mx-2" />

            <NavLink 
              to="/contact" 
              onClick={() => setIsOpen(false)}
              className="px-4 py-3 font-bold uppercase tracking-widest text-[12px] text-slate-500 hover:bg-slate-50 rounded-sm"
            >
              Contact
            </NavLink>
          </motion.div>
        </>
        )}
      </AnimatePresence>
    </nav>
  );

};

export default Navbar;
